// cspell:ignore relayout
import type { TextEngineService } from '../../text-engine';
import type { AssetGetters } from '../assets';
import type { TextRenderInput, TextRenderOutput, TextRenderSnapshot } from '../types';
import { resolveInputFonts } from '../utils';
import { resolveArtTextVisual } from './art-text';
import { drawBackground } from './background';
import { createLayoutCacheKey, createPaintCacheKey, patchSnapshotColors } from './cache';
import type { RenderContext } from './context';
import { resolveFill } from './fill';
import { resolveFilter } from './filter';
import { drawGlyphs, drawStrokes, resolveGlyph } from './glyph';
import type { GlyphCache } from './glyph/cache';
import { resolveHighlightVisual } from './highlight';
import { resolveListMarker } from './list-marker';
import { shapeListMarker, type ListMarkerShaping } from './list-marker/shape';
import {
    applyBufferUpdate,
    classifyBufferUpdate,
    createSnapshotBuffer,
    layoutSnapshotBuffer,
    prepareBufferInput,
    readSnapshot,
    type BufferCacheEntry,
} from './snapshot';
import { createSurface, foldGeometry } from './surface';

/**
 * 为完整 Text Render Input 创建输出缓存键。
 *
 * @param input - 已完成字体解析的当前文字渲染输入。
 * @returns 可比较连续等值输入的稳定字符串。
 */
const createOutputCacheKey = (input: TextRenderInput): string => JSON.stringify(input);

/**
 * 管理单个 Text Renderer 的同步排版、视觉特性编排与缓存复用。
 */
export class Pipeline {
    /**
     * 当前 Renderer 独占的常驻 TextBuffer 及其最后一次完整成功应用的配置。
     *
     * 只有 apply、layout、宿主测量和 snapshot 读取全部成功后才写入；失败时立即释放 Buffer 并清空引用。
     */
    private bufferEntry: BufferCacheEntry | null = null;

    /**
     * 常驻 Buffer 与已发布结果所属的 Engine 实例。
     */
    private observedEngine: ReturnType<TextEngineService['getEngine']> = null;

    /**
     * 常驻 Buffer 与已发布结果所属的 Engine 字体状态版本。
     */
    private observedFontRevision = -1;
    /**
     * 上一次成功排版输入的几何缓存键。
     */
    private lastLayoutCacheKey: string | null = null;

    /**
     * 上一次成功 snapshot 使用的文字颜色缓存键。
     */
    private lastPaintCacheKey: string | null = null;

    /**
     * 上一次成功完整输出的缓存键。
     */
    private lastOutputCacheKey: string | null = null;

    /**
     * 与 `lastSnapshot` 对应的最近一次字体解析后输入。
     */
    private lastResolvedInput: TextRenderInput | null = null;

    /**
     * 最近一次成功排版或颜色补丁产生的只读 snapshot。
     */
    private lastSnapshot: TextRenderSnapshot | null = null;

    /**
     * 与 `lastSnapshot` 同轮生成的列表 marker 塑形事实。
     */
    private lastListMarkerShaping: ListMarkerShaping | null = null;

    /**
     * 最近一次成功生成的完整输出。
     */
    private lastOutput: TextRenderOutput | null = null;

    /**
     * 创建一个 Renderer 私有的同步 Pipeline。
     *
     * @param textEngine - 提供当前可用文字引擎的编辑器服务。
     * @param glyphCache - 编辑器实例内跨 Renderer 共享的字形模板缓存。
     * @param layerId - 当前宿主图层 ID，用作 SVG 资源命名空间。
     * @returns Pipeline 实例。
     */
    constructor(
        private readonly textEngine: TextEngineService,
        private readonly glyphCache: GlyphCache,
        private readonly layerId: string,
    ) {}

    /**
     * 解析字体并同步生成或复用当前 Text Render Snapshot。
     *
     * 每次非空调用先同步 Engine identity 与 font revision，确保字体状态变化后旧 Buffer、snapshot 和
     * glyph templates 已失效。几何 key 相同时直接复用 snapshot，paint-only 只更新颜色；否则按
     * reuse/relayout/reshape 计划更新常驻 Buffer。Engine 暂时不可用时，只有完全相同的已发布几何
     * 仍可在前面的缓存分支复用，新输入返回 null。
     *
     * @param input - 当前完整 Text Render Input。
     * @param fontFamilies - 声明字体族到当前可用字体族的映射。
     * @returns 当前 snapshot；文字引擎不可用时返回 null。
     */
    layout(input: TextRenderInput, fontFamilies: ReadonlyMap<string, string>): TextRenderSnapshot | null {
        const resolvedInput = resolveInputFonts(input, fontFamilies);
        const layoutCacheKey = createLayoutCacheKey(resolvedInput);
        const paintCacheKey = createPaintCacheKey(resolvedInput);
        const engine = this.textEngine.getEngine();

        if (engine) this.syncEngine(engine, this.textEngine.getFontRevision?.() ?? 0);

        // 几何输入未变时复用 snapshot；文字颜色变化只补丁绘制事实，避免重新排版。
        if (layoutCacheKey === this.lastLayoutCacheKey && this.lastSnapshot) {
            this.lastResolvedInput = resolvedInput;
            if (paintCacheKey !== this.lastPaintCacheKey) {
                this.lastSnapshot = patchSnapshotColors(this.lastSnapshot, resolvedInput);
                this.lastPaintCacheKey = paintCacheKey;
            }
            return this.lastSnapshot;
        }

        if (!engine || engine.isDisposed) return null;

        // 列表 marker 塑形先确定正文可用空间；Pipeline 保证它与随后生成的 snapshot 同轮发布。
        const listMarkerShaping = shapeListMarker(engine, resolvedInput);
        const listMarkerIndent = listMarkerShaping?.indent ?? 0;
        const prepared = prepareBufferInput(resolvedInput, listMarkerIndent);
        const isNewBuffer = !this.bufferEntry;
        const buffer = this.bufferEntry?.buffer ?? createSnapshotBuffer(engine, resolvedInput);

        try {
            const plan = classifyBufferUpdate(this.bufferEntry?.applied, prepared.applied);
            applyBufferUpdate(buffer, resolvedInput, prepared, plan, isNewBuffer);
            if (plan.kind !== 'reuse') {
                layoutSnapshotBuffer(buffer, resolvedInput, prepared.applied.relayout.inlineSize);
            }
            const nextSnapshot = readSnapshot(buffer, resolvedInput, prepared.spans, listMarkerIndent, this.glyphCache);
            if (!nextSnapshot) throw new Error('Text engine returned no usable output.');
            const snapshot = patchSnapshotColors(nextSnapshot, resolvedInput);

            this.bufferEntry = { buffer, applied: prepared.applied };

            // Snapshot 与列表 marker 塑形必须在全部排版成功后同轮发布，避免后续 output 组合不同轮次的事实。
            this.lastLayoutCacheKey = layoutCacheKey;
            this.lastPaintCacheKey = paintCacheKey;
            this.lastResolvedInput = resolvedInput;
            this.lastSnapshot = snapshot;
            this.lastListMarkerShaping = listMarkerShaping;
            return snapshot;
        } catch (error) {
            // Buffer 可能已经被部分 setter、layout 或宿主测量修改，失败后不能再猜测其内部状态。
            buffer.dispose();
            this.bufferEntry = null;
            throw error;
        }
    }

    /**
     * 使用当前排版事实同步准备并挂载全部视觉特性。
     *
     * @param input - 当前完整 Text Render Input。
     * @param fontFamilies - 声明字体族到当前可用字体族的映射。
     * @param assets - 当前输入已经就绪的素材读取方法。
     * @returns 当前完整输出；文字引擎不可用时返回 null。
     */
    render(
        input: TextRenderInput,
        fontFamilies: ReadonlyMap<string, string>,
        assets: AssetGetters,
    ): TextRenderOutput | null {
        const snapshot = this.layout(input, fontFamilies);
        if (!snapshot) return null;

        const resolvedInput = this.lastResolvedInput ?? input;
        const outputCacheKey = createOutputCacheKey(resolvedInput);
        if (outputCacheKey === this.lastOutputCacheKey && this.lastOutput?.snapshot === snapshot) {
            return this.lastOutput;
        }

        const context: RenderContext = { input: resolvedInput, snapshot, namespace: this.layerId };

        // 准备阶段只解析本轮事实；每个视觉特性入口在一轮 render 中只调用一次。
        const artText = resolveArtTextVisual(context, assets.getArtText);
        const highlight = resolveHighlightVisual(context, assets.getHighlight);
        const listMarker = resolveListMarker(context, this.lastListMarkerShaping, assets.getListMarker);
        const glyph = resolveGlyph(context, artText?.transforms);
        const filter = resolveFilter(resolvedInput, this.layerId);
        const fill = resolveFill(context, assets.getTexture);
        const visuals = { artText, highlight, listMarker, glyph, filter, fill };

        // 几何折叠完成后才能创建带最终 viewBox 与 filter 区域的 surface。
        const geometry = foldGeometry(context, visuals);
        const surface = createSurface(geometry.renderBox, visuals.filter, snapshot);

        // 挂载顺序显式表达视觉层序，任何特性都不再遍历或搬运其他特性的节点。
        drawBackground(surface.background, resolvedInput, geometry.layoutBox);
        visuals.artText?.draw(surface.material);
        visuals.highlight?.draw(surface.material);
        drawStrokes(surface.body, visuals.glyph.items, visuals.glyph.strokes);
        visuals.fill.draw(surface.body, geometry.layoutBox, visuals.artText?.paintBounds ?? null, visuals.glyph.items);
        drawGlyphs(surface.body, visuals.glyph.items, visuals.fill.fillId !== null);

        if (visuals.listMarker?.markerPaths.length) {
            surface.characterMarker.setAttribute('data-list-marker', 'character');
            visuals.listMarker.markerPaths.forEach((markerPaths) => {
                drawStrokes(surface.characterMarker, markerPaths, visuals.glyph.strokes);
                drawGlyphs(surface.characterMarker, markerPaths, false, visuals.fill.fillId);
            });
        }
        visuals.listMarker?.drawGraphic?.(surface.graphicMarker);

        const output = Object.freeze({
            snapshot,
            svg: surface.root,
            ...geometry,
        });
        this.lastOutputCacheKey = outputCacheKey;
        this.lastOutput = output;
        return output;
    }

    /**
     * 清除当前 Pipeline 持有的全部同步排版与输出缓存。
     *
     * reset 是 Buffer 的生命周期终点：empty 输入、Renderer dispose 和 Service 卸载都会经过这里。
     * GlyphCache 由 TextRenderService 共享管理，此处只重置本 Pipeline 的 Engine 观察值与发布状态。
     *
     * @returns void。
     */
    reset(): void {
        this.bufferEntry?.buffer.dispose();
        this.bufferEntry = null;
        this.observedEngine = null;
        this.observedFontRevision = -1;
        this.clearPublishedState();
    }

    /**
     * 将 Pipeline 和共享 GlyphCache 同步到当前 Engine 字体状态。
     *
     * Engine 实例或 font revision 变化都可能改变 shaping 与绘制模板，因此必须释放常驻 Buffer 并清除
     * 所有已发布 snapshot/output。首次观察 Engine 时没有旧状态需要释放，只记录当前身份。
     *
     * @param engine - 当前 ready 且未释放的 Engine。
     * @param revision - Engine 当前字体状态版本。
     * @returns 无返回值。
     */
    private syncEngine(engine: NonNullable<ReturnType<TextEngineService['getEngine']>>, revision: number): void {
        this.glyphCache.sync(engine, revision);
        if (this.observedEngine && (this.observedEngine !== engine || this.observedFontRevision !== revision)) {
            this.bufferEntry?.buffer.dispose();
            this.bufferEntry = null;
            this.clearPublishedState();
        }
        this.observedEngine = engine;
        this.observedFontRevision = revision;
    }

    /**
     * 清除所有由旧 Buffer 或旧字体状态派生的已发布事实。
     *
     * 该方法不释放 Buffer；调用方必须根据自身生命周期先决定释放、替换或继续持有 Buffer。
     *
     * @returns 无返回值。
     */
    private clearPublishedState(): void {
        this.lastLayoutCacheKey = null;
        this.lastPaintCacheKey = null;
        this.lastOutputCacheKey = null;
        this.lastResolvedInput = null;
        this.lastSnapshot = null;
        this.lastListMarkerShaping = null;
        this.lastOutput = null;
    }
}
